import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NavIcon, useBrandTokens, SidebarToggleButton } from '../../../shell/shared';
import { usePatientShellContext } from '../../../shell/PatientShellLayout';
import { useAuth, usePermissions } from '@helios/auth-firebase-web';
import { FilesTab } from '../consultation/FilesTab';
import { InvoicesTab } from '../consultation/InvoicesTab';
import { EncounterFormsTab } from './EncounterFormsTab';
import { EncounterVitalsTab } from './EncounterVitalsTab';
import { EncounterNotesTab } from './EncounterNotesTab';
import { EncounterSymptomsTab } from './EncounterSymptomsTab';
import { EncounterDiagnosesTab } from './EncounterDiagnosesTab';
import { EncounterChartingTab } from './EncounterChartingTab';
import { EncounterPrescriptionsTab } from './EncounterPrescriptionsTab';
import { EncounterPharmacyTab } from './EncounterPharmacyTab';
import { usePowerSyncQuery, usePowerSyncDb } from '../../../adapters/powersync';
import { useInvoiceModuleEnabled } from '../../../hooks/useClinicMainModules';
import { EncounterLabsTab } from './EncounterLabsTab';
import {
  qEncounterById,
  qAppointmentById,
  qPatientWorkspaceSettings,
  mapPatientWorkspaceSettingsToUI,
  appendDatedSummary,
  qAppConfig,
  getClinicClassification,
  type PatientEncounterRow,
  type AppointmentRow,
  type PatientWorkspaceSettingsRow,
  type AppConfigRow,
} from '@helios/data-access';
import { ChiefComplaintVoicePanel } from './ChiefComplaintVoicePanel';
import {
  useEncounterTabConfig,
  DEFAULT_TABS,
  HIDDEN_SUBMODULE_KEYS,
} from './useEncounterTabConfig';
import { PERM } from '../../../auth/permissionMap';
import { guidedPresetForClinicType } from './guided-note-presets';
import { VisitNoteSettingsDrawer } from './VisitNoteSettingsDrawer';
import { GuidedNoteRenderer } from './GuidedNoteRenderer';
import { DentalChartingHero } from './DentalChartingHero';
import { AestheticVisualHero } from './AestheticVisualHero';
import { VisitBillingChip } from './VisitBillingChip';
import { VisitBillingPanel } from './VisitBillingPanel';
import { UsedSuppliesPanel } from './UsedSuppliesPanel';
import { TreatmentPlanSection } from './TreatmentPlanSection';
import { GuidedInvoiceSummary } from './GuidedInvoiceSummary';
import { GuidedPaymentSummary } from './GuidedPaymentSummary';
import { DeleteEncounterModal } from './DeleteEncounterModal';
import { deleteEncounterCascade } from '../../../services/delete-encounter-service';
import { closeEncounter } from '../../../services/encounter-service';

// ─── Helpers ───

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const TYPE_LABELS: Record<string, string> = {
  consultation: 'Consultation',
  procedure: 'Procedure',
  follow_up: 'Follow-up',
  triage: 'Triage',
};

// ─── Tabs ───

type TabKey =
  | 'summary'
  // PRD.233 — Chief Complaint is its own first-class section.
  | 'chief_complaint'
  | 'charting'
  | 'notes'
  | 'symptoms'
  | 'diagnoses'
  | 'vitals'
  | 'prescriptions'
  | 'treatment_plan'
  | 'forms'
  | 'files'
  | 'invoices'
  | 'pharmacy'
  | 'payments'
  // PRD.305 — Labs sub-module.
  | 'labs';

// ─── Status Badge ───

type DisplayStatus = 'Open' | 'Closed' | 'In Progress';

const STATUS_STYLES: Record<DisplayStatus, { bg: string; color: string }> = {
  Open: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
  Closed: { bg: 'var(--color-neutral-bg)', color: 'var(--text-muted)' },
  'In Progress': { bg: 'var(--color-info-bg)', color: 'var(--color-info)' },
};

function StatusBadge({ status }: { status: string }) {
  const display: DisplayStatus = status === 'closed' ? 'Closed' : 'Open';
  const s = STATUS_STYLES[display];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        color: s.color,
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semi)',
        lineHeight: '20px',
      }}
    >
      {display}
    </span>
  );
}

// ─── Consult Lifecycle (PRD.217) ───
//
// Derives consult state from the two encounter timestamps and renders:
//   - a state badge (Not Started / In Progress / Ended)
//   - a Start Consult or End Consult primary action (whichever is valid)
// Once the consult has ended, both buttons are hidden — per PRD §9.3, the
// header should not keep presenting Start/End as if the consult is still
// active. Reopen/correction is deferred (PRD §9.4).

type ConsultState = 'not_started' | 'in_progress' | 'ended';

function deriveConsultState(encounter: PatientEncounterRow): ConsultState {
  // A closed encounter is terminal regardless of the consult timestamps — e.g.
  // closed by a payment before "Start Consult" was ever clicked. Treat as ended
  // so the lifecycle shows no Start/End action.
  if (encounter.status === 'closed') return 'ended';
  if (encounter.consult_started_at && encounter.consult_ended_at) return 'ended';
  if (encounter.consult_started_at) return 'in_progress';
  return 'not_started';
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString(
    'en-US',
    { hour: 'numeric', minute: '2-digit' },
  )}`;
}

function formatConsultDuration(startIso: string, endIso: string): string {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

const CONSULT_BADGE_STYLES: Record<ConsultState, { bg: string; color: string; label: string }> = {
  not_started: {
    bg: 'var(--color-neutral-bg, #f3f4f6)',
    color: 'var(--text-muted)',
    label: 'Consult: Not Started',
  },
  in_progress: {
    bg: 'var(--color-success-bg)',
    color: 'var(--color-success-text)',
    label: 'Consult: In Progress',
  },
  ended: {
    bg: 'var(--color-neutral-bg, #f3f4f6)',
    color: 'var(--text-muted)',
    label: 'Consult: Ended',
  },
};

function ConsultLifecycleBadge({ encounter }: { encounter: PatientEncounterRow }) {
  const state = deriveConsultState(encounter);
  const s = CONSULT_BADGE_STYLES[state];
  const tooltipParts: string[] = [];
  if (encounter.consult_started_at) {
    tooltipParts.push(`Started: ${formatTimestamp(encounter.consult_started_at)}`);
  }
  if (encounter.consult_ended_at) {
    tooltipParts.push(`Ended: ${formatTimestamp(encounter.consult_ended_at)}`);
  }
  if (encounter.consult_started_at && encounter.consult_ended_at) {
    tooltipParts.push(
      `Duration: ${formatConsultDuration(encounter.consult_started_at, encounter.consult_ended_at)}`,
    );
  }
  const tooltip = tooltipParts.length > 0 ? tooltipParts.join('\n') : 'Consult has not started yet';
  const durationSuffix =
    state === 'ended' && encounter.consult_started_at && encounter.consult_ended_at
      ? ` · ${formatConsultDuration(encounter.consult_started_at, encounter.consult_ended_at)}`
      : '';
  return (
    <span
      title={tooltip}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 10px',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        color: s.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        lineHeight: '20px',
        whiteSpace: 'nowrap',
      }}
    >
      {state === 'in_progress' && (
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'currentColor',
          }}
        />
      )}
      {s.label}
      {durationSuffix}
    </span>
  );
}

function ConsultLifecycleAction({
  encounter,

  db,
}: {
  encounter: PatientEncounterRow;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: any;
}) {
  const state = deriveConsultState(encounter);
  const [pending, setPending] = useState(false);
  const { user } = useAuth();

  const startConsult = useCallback(async () => {
    // Guardrails: do nothing if consult already started.
    if (!db || pending) return;
    if (encounter.consult_started_at) return;
    setPending(true);
    try {
      const now = new Date().toISOString();
      await db.execute(
        'UPDATE patient_encounters SET consult_started_at = ?, updated_at = ? WHERE id = ?',
        [now, now, encounter.id],
      );
    } finally {
      setPending(false);
    }
  }, [db, encounter.id, encounter.consult_started_at, pending]);

  const endConsult = useCallback(async () => {
    // Guardrails: only end when in progress.
    if (!db || pending) return;
    if (!encounter.consult_started_at || encounter.consult_ended_at) return;
    setPending(true);
    try {
      // End Consult closes the consultation: stamps consult_ended_at AND sets
      // status='closed' (with audit fields), so the encounter is actually Closed.
      await closeEncounter(db, {
        encounterId: encounter.id,
        userId: user?.uid,
        reason: 'end_consult',
        endConsult: true,
      });
    } finally {
      setPending(false);
    }
  }, [
    db,
    encounter.id,
    encounter.consult_started_at,
    encounter.consult_ended_at,
    pending,
    user?.uid,
  ]);

  if (state === 'ended') return null;

  if (state === 'not_started') {
    return (
      <button
        onClick={startConsult}
        disabled={pending}
        title="Record the moment the consult begins"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 32,
          padding: '0 12px',
          borderRadius: 'var(--radius-sm)',
          border: 'none',
          background: 'var(--color-primary)',
          color: '#fff',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semi)',
          cursor: pending ? 'wait' : 'pointer',
          opacity: pending ? 0.7 : 1,
          flexShrink: 0,
        }}
      >
        <PlayIcon />
        Start Consult
      </button>
    );
  }

  // in_progress
  return (
    <button
      onClick={endConsult}
      disabled={pending}
      title="Mark the consult as ended"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 32,
        padding: '0 12px',
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--color-primary)',
        background: 'var(--bg-surface)',
        color: 'var(--color-primary)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semi)',
        cursor: pending ? 'wait' : 'pointer',
        opacity: pending ? 0.7 : 1,
        flexShrink: 0,
      }}
    >
      <StopIcon />
      End Consult
    </button>
  );
}

function PlayIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="6" y="6" width="12" height="12" rx="1" />
    </svg>
  );
}

// ─── Summary Tab ───

// ─── Accordion icon map ───

const ACCORDION_ICONS: Record<string, string> = {
  summary: 'layout-grid',
  // PRD.233 — Chief Complaint is its own first-class section.
  chief_complaint: 'message-square',
  vitals: 'heart',
  charting: 'edit',
  notes: 'file-text',
  symptoms: 'clipboard', // PRD.214
  diagnoses: 'stethoscope', // PRD.214
  prescriptions: 'clipboard',
  treatment_plan: 'clipboard',
  forms: 'file-text',
  files: 'folder-open',
  invoices: 'credit-card',
  pharmacy: 'heart',
  payments: 'credit-card',
  labs: 'clipboard', // PRD.305
};

// ─── Summary — compact metadata strip (PRD.233) ───
//
// PRD.233 §5.1 / §6.2 — Summary should read as a compact metadata strip
// (~1–2 rows) and not visually compete with substantive clinical
// modules. We render the same fields as before — Date · Time · Duration
// on row 1, Type · Provider on row 2 — as inline label:value pairs.
// Chief Complaint has been lifted out into its own full-width section
// (see `ChiefComplaintSection` below) so dictated long-form complaint
// text gets the horizontal room it deserves.

const S_PAIR_LABEL: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontWeight: 500,
  marginRight: 6,
};

const S_PAIR_VALUE: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  fontWeight: 500,
};

const S_PAIR_WRAP: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'baseline',
  whiteSpace: 'nowrap',
};

function SummaryPair({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <span style={S_PAIR_WRAP}>
      <span style={S_PAIR_LABEL}>{label}</span>
      <span style={S_PAIR_VALUE}>{value}</span>
    </span>
  );
}

function SummaryTab({
  encounter,
  appointment,
}: {
  encounter: PatientEncounterRow;
  appointment?: AppointmentRow | null;
}) {
  const duration =
    encounter.ended_at && encounter.started_at
      ? `${Math.round((new Date(encounter.ended_at).getTime() - new Date(encounter.started_at).getTime()) / 60000)} min`
      : 'Ongoing';

  return (
    <div
      style={{
        padding: 'var(--sp-3) var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
      }}
    >
      {/* Row 1 — Date · Time · Duration */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          columnGap: 'var(--sp-5)',
          rowGap: 'var(--sp-1)',
        }}
      >
        <SummaryPair label="Date" value={formatDate(encounter.started_at)} />
        <SummaryPair label="Time" value={formatTime(encounter.started_at) || '—'} />
        <SummaryPair label="Duration" value={duration} />
      </div>

      {/* Row 2 — Type · Provider */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          columnGap: 'var(--sp-5)',
          rowGap: 'var(--sp-1)',
        }}
      >
        <SummaryPair label="Type" value={TYPE_LABELS[encounter.type] ?? encounter.type} />
        <SummaryPair label="Provider" value={encounter.provider_name ?? 'Unassigned'} />
      </div>

      {/* Optional appointment context — only rendered when an appointment
          is linked, kept inline so it doesn't claim a whole column. */}
      {appointment && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            columnGap: 'var(--sp-5)',
            rowGap: 'var(--sp-1)',
            paddingTop: 'var(--sp-2)',
            borderTop: '1px dashed var(--border)',
          }}
        >
          <SummaryPair
            label="Scheduled"
            value={`${appointment.date ?? '—'} · ${appointment.start_time ?? '—'}`}
          />
          {appointment.duration_minutes != null && (
            <SummaryPair label="Appt. Duration" value={`${appointment.duration_minutes} min`} />
          )}
          {appointment.notes && (
            <span style={S_PAIR_WRAP}>
              <span style={S_PAIR_LABEL}>Notes</span>
              <span style={{ ...S_PAIR_VALUE, whiteSpace: 'normal' }}>{appointment.notes}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Chief Complaint — full-width section (PRD.233 §5.2) ───
//
// Lifted out of Summary so it gets its own horizontal room for typed +
// dictated content. The inner `ChiefComplaintField` is unchanged —
// persistence (`patient_encounters.chief_complaint`) and voice-to-text
// behavior is identical; only the framing is new.

function ChiefComplaintSection({ encounter }: { encounter: PatientEncounterRow }) {
  // No inner heading — the surrounding layout (tab label, accordion
  // header, or guided-note card title) already says "Chief Complaint".
  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-5)',
      }}
    >
      <ChiefComplaintField encounter={encounter} />
    </div>
  );
}

// ─── Chief Complaint field ───
// Voice-to-text entry with summarization (PRD.212) + persistence on
// patient_encounters.chief_complaint (PRD.213).

function ChiefComplaintField({ encounter }: { encounter: PatientEncounterRow }) {
  const { db } = usePowerSyncDb();
  const initial = encounter.chief_complaint ?? '';
  const [value, setValue] = useState(initial);
  const valueRef = useRef(initial);
  const lastSavedRef = useRef(initial);

  const saveChiefComplaint = useCallback(
    async (text: string) => {
      if (!db) return;
      if (text === lastSavedRef.current) return;
      const now = new Date().toISOString();
      await db.execute(
        'UPDATE patient_encounters SET chief_complaint = ?, updated_at = ? WHERE id = ?',
        [text || null, now, encounter.id],
      );
      lastSavedRef.current = text;
    },
    [db, encounter.id],
  );

  // PRD.274 final rule — Chief Complaint is plain editable text. Append one
  // dated summary snapshot; it becomes ordinary text immediately (no marker,
  // no regeneration). Append-once is enforced by the panel/card via the
  // conversation's summary_appended_at, not by parsing this string.
  const appendSummary = useCallback(
    (dateTime: string, summary: string) => {
      const next = appendDatedSummary(valueRef.current, dateTime, summary);
      if (next === valueRef.current) return;
      valueRef.current = next;
      setValue(next);
      void saveChiefComplaint(next);
    },
    [saveChiefComplaint],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <textarea
        placeholder="No chief complaint recorded"
        value={value}
        onChange={(e) => {
          valueRef.current = e.target.value;
          setValue(e.target.value);
        }}
        onBlur={() => void saveChiefComplaint(valueRef.current)}
        rows={4}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '8px 10px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text)',
          resize: 'vertical',
          fontFamily: 'inherit',
          outline: 'none',
          lineHeight: 'var(--line-height-body)',
        }}
      />

      {/* PRD.274 — recording + conversations live below. A conversation's first
          extraction appends one dated snapshot into the editable Chief Complaint
          above (append-once); refresh never rewrites it. */}
      <ChiefComplaintVoicePanel encounter={encounter} onAppendSummary={appendSummary} />
    </div>
  );
}

// ─── Placeholder Tabs ───

const PLACEHOLDER_MESSAGES: Record<string, string> = {
  'Treatment Plan': 'No treatment plan yet',
  Payments: 'No payments recorded',
};

function PlaceholderTab({ title }: { title: string }) {
  return (
    <div
      style={{
        padding: 'var(--sp-8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-base)',
      }}
    >
      {PLACEHOLDER_MESSAGES[title] ?? `${title} content will appear here`}
    </div>
  );
}

// ─── Main Component ───

// ─── Main ───

export function EncounterDetailPage() {
  const { id: patientId, encounterId } = useParams<{ id: string; encounterId: string }>();
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { collapsed: sidebarCollapsed, onToggleSidebar } = usePatientShellContext();

  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabKey | null;
  const [activeTab, setActiveTab] = useState<TabKey>(tabParam ?? 'summary');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [billingPanelOpen, setBillingPanelOpen] = useState(false);
  const [usedSuppliesPanelOpen, setUsedSuppliesPanelOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';

  // PRD.282 / PRD.300 — the clinic classification is the source of the Visit
  // Note default: Guided layout, with the primary clinic type selecting which
  // guided note (dental / aesthetic / general) and the shared clinic-type
  // preset selecting the sub-modules. Derived on every read, never persisted
  // per user, so a clinic-type change reaches everyone on default.
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const clinicClassification = useMemo(
    () => getClinicClassification(appConfigRows[0] ?? null),
    [appConfigRows],
  );
  const defaultGuidedNotePreset = useMemo(
    () => guidedPresetForClinicType(clinicClassification.primaryClinicType),
    [clinicClassification],
  );

  // Clinic-level Patient Consultation Tabs visibility (PRD.181). Hides
  // sections from every user in the clinic regardless of their per-user
  // encounter tab config. Falls back to "all visible" until the workspace
  // settings row arrives so the UI never blanks out during initial load.
  const { data: workspaceSettingsRows = [] } = usePowerSyncQuery<PatientWorkspaceSettingsRow>(
    qPatientWorkspaceSettings(),
  );
  const clinicVisibleConsultKeys = useMemo<Set<string> | null>(() => {
    if (workspaceSettingsRows.length === 0) return null;
    const ui = mapPatientWorkspaceSettingsToUI(workspaceSettingsRows[0]);
    const allowed = new Set<string>();
    if (ui.consultShowSummary) allowed.add('summary');
    if (ui.consultShowVitals) allowed.add('vitals');
    if (ui.consultShowCharting) allowed.add('charting');
    if (ui.consultShowNotes) allowed.add('notes');
    if (ui.consultShowPrescriptions) allowed.add('prescriptions');
    if (ui.consultShowTreatmentPlan) allowed.add('treatment_plan');
    if (ui.consultShowForms) allowed.add('forms');
    if (ui.consultShowFiles) allowed.add('files');
    if (ui.consultShowInvoices) allowed.add('invoices');
    if (ui.consultShowPayments) allowed.add('payments');
    // PRD.282 — the remaining Visit Note sub-modules are now clinic-gateable
    // too (default true, so existing clinics are unaffected until an admin turns
    // one off). Per-user/guided tab config still narrows further within these.
    if (ui.consultShowChiefComplaint) allowed.add('chief_complaint');
    if (ui.consultShowSymptoms) allowed.add('symptoms');
    if (ui.consultShowDiagnoses) allowed.add('diagnoses');
    if (ui.consultShowPharmacy) allowed.add('pharmacy');
    // PRD.305 — Labs is a normal clinic-gateable Visit Note sub-module.
    if (ui.consultShowLabs) allowed.add('labs');
    return allowed;
  }, [workspaceSettingsRows]);

  // PRD.283 — clinic-level Invoice module toggle. When disabled, the Billing
  // ("Invoices") entry point is hidden from Consultation. Restrictive only:
  // defaults to enabled, and per-user/clinic tab config still narrows further.
  const clinicInvoiceEnabled = useInvoiceModuleEnabled();
  // PRD.305 D5 — note what is deliberately ABSENT here: the Laboratory module
  // toggle. Disabling Laboratory removes the OPERATIONAL surfaces, not the
  // clinical history, so the Labs section stays in `availableKeys` and instead
  // degrades to published-results-only inside `resolveLaboratoryCapabilities`.
  // Subtracting it here would hide a patient's published results along with the
  // workflow, which is exactly what the PRD asks us not to do.

  // PRD.300 §5.9 — RBAC is authoritative over any saved preference. Only
  // sub-modules that map onto a real CSV permission are gated; the rest are
  // covered by the route's own `patientWorkspace` requirement. Applied only
  // once permissions are READY — `canAccess` fails closed while loading, and
  // gating on that would blank the workspace on every page load.
  const { canAccess, status: permissionStatus } = usePermissions();
  const rbacAllowedKeys = useMemo<Set<string> | null>(() => {
    if (permissionStatus !== 'ready') return null;
    const denied = new Set<string>();
    if (!canAccess(PERM.billing)) {
      denied.add('invoices');
      denied.add('payments');
    }
    if (!canAccess(PERM.pharmacy)) denied.add('pharmacy');
    if (!canAccess(PERM.vitals)) denied.add('vitals');
    if (!canAccess(PERM.forms)) denied.add('forms');
    // PRD.305 — reading laboratory orders/results is the entry requirement for
    // the Labs section; the ACTIONS inside it are gated separately by
    // `resolveLaboratoryCapabilities`.
    if (!canAccess(PERM.labOrders)) denied.add('labs');
    return new Set(DEFAULT_TABS.map((t) => t.key).filter((k) => !denied.has(k)));
  }, [canAccess, permissionStatus]);

  // PRD.300 §5.10 — the single availability set handed to the preference
  // resolver: hidden sub-modules ∩ clinic module config ∩ feature flags ∩ RBAC.
  // A `null` clinic/RBAC input means "not resolved yet" and contributes no
  // constraint; HIDDEN_SUBMODULE_KEYS always applies, so this is never null.
  const availableKeys = useMemo<Set<string> | null>(() => {
    const all = DEFAULT_TABS.map((t) => t.key);
    return new Set(
      all.filter((key) => {
        if (HIDDEN_SUBMODULE_KEYS.has(key)) return false;
        if (clinicVisibleConsultKeys && !clinicVisibleConsultKeys.has(key)) return false;
        if (rbacAllowedKeys && !rbacAllowedKeys.has(key)) return false;
        if (!clinicInvoiceEnabled && key === 'invoices') return false;
        return true;
      }),
    );
  }, [clinicVisibleConsultKeys, rbacAllowedKeys, clinicInvoiceEnabled]);

  const {
    settings: tabSettings,
    visibleTabs,
    visibleGuidedTabs: guidedVisibleTabs,
    layoutConfig,
    updateSettings,
    resetToDefault,
  } = useEncounterTabConfig(orgId, userId, clinicClassification, availableKeys);
  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const def = layoutConfig.defaultOpenSection;
    if (def === '__all__') return new Set(visibleTabs.map((t) => t.key));
    return new Set(def ? [def] : ['summary']);
  });

  // Sync open sections when layout settings change (e.g. after Save in drawer)
  useEffect(() => {
    const def = layoutConfig.defaultOpenSection;
    if (def === '__all__') {
      setOpenSections(new Set(visibleTabs.map((t) => t.key)));
    } else if (def) {
      setOpenSections(new Set([def]));
    }
  }, [layoutConfig.defaultOpenSection, layoutConfig.accordionMode]);

  // Keep the UI in sync with the URL's ?tab= hint. Needed because the shell
  // can keep this page mounted across navigations to the same encounter (e.g.
  // clicking an invoice row in the sidebar while already on that encounter)
  // — without this, the state would stay on whatever tab/section was last
  // shown and the hint in the URL would be ignored. Handles both layout
  // styles: flipping the active tab and expanding the matching accordion
  // section.
  useEffect(() => {
    if (!tabParam) return;
    setActiveTab(tabParam);
    if (layoutConfig.style === 'accordion') {
      setOpenSections((prev) => {
        if (prev.has(tabParam)) return prev;
        if (layoutConfig.accordionMode === 'single') return new Set([tabParam]);
        const next = new Set(prev);
        next.add(tabParam);
        return next;
      });
      // Scroll the section into view after the expansion has painted.
      const t = window.setTimeout(() => {
        const el = document.getElementById(`encounter-section-${tabParam}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
      return () => window.clearTimeout(t);
    }
  }, [tabParam, layoutConfig.style, layoutConfig.accordionMode]);

  // Safe fallback: if the active tab gets hidden by the clinic's Patient
  // Consultation Tabs settings (PRD.181), reroute to the first remaining
  // visible tab so the user never lands on a blank section.
  useEffect(() => {
    if (visibleTabs.length === 0) return;
    if (visibleTabs.some((t) => t.key === activeTab)) return;
    setActiveTab(visibleTabs[0].key as TabKey);
  }, [activeTab, visibleTabs]);

  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncounterById(encounterId ?? ''),
  );
  const encounter = encounterRows[0] ?? null;

  // Fetch linked appointment (if any) for appointment-aware Summary
  const appointmentId = encounter?.appointment_id ?? '';
  const { data: appointmentRows = [] } = usePowerSyncQuery<AppointmentRow>(
    qAppointmentById(appointmentId),
  );
  const appointment = appointmentId ? (appointmentRows[0] ?? null) : null;

  const handleDeleteEncounter = useCallback(
    async (opts: { deleteAppointment: boolean; deleteQueueEntry: boolean }) => {
      if (!db || !encounterId || !orgId) return;
      setIsDeleting(true);
      try {
        const result = await deleteEncounterCascade({
          db,
          orgId,
          encounterId,
          deleteAppointment: opts.deleteAppointment,
          deleteQueueEntry: opts.deleteQueueEntry,
        });
        setDeleteModalOpen(false);
        // Build toast message
        const parts = ['Encounter deleted'];
        if (result.deletedAppointment) parts.push('+ appointment');
        if (result.deletedQueueEntry) parts.push('+ queue entry');
        if (result.error) parts.push(`(${result.error})`);
        console.log('[DeleteEncounter]', parts.join(' '));
        // Navigate back
        navigate(`/patient/${patientId}/encounters`);
      } catch (err) {
        console.error('[DeleteEncounter] Failed:', err);
      } finally {
        setIsDeleting(false);
      }
    },
    [db, orgId, encounterId, patientId, navigate],
  );

  if (!encounter) {
    return (
      <div
        style={{
          padding: 'var(--sp-8)',
          textAlign: 'center',
          color: 'var(--text-muted)',
        }}
      >
        Visit note not found
      </div>
    );
  }

  const typeLabel = TYPE_LABELS[encounter.type] ?? encounter.type;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header — see `.encounter-header` in index.css. One row when there's
          room; below ~1000px of header width the identity line takes the first
          row and the subtitle + actions drop to a second, so the gear/delete
          buttons stay reachable on a tablet. */}
      <div className="encounter-header">
        <div className="encounter-header-inner">
          <div className="encounter-header-top">
            <div className="encounter-header-id">
              {/* Sidebar toggle */}
              <SidebarToggleButton collapsed={sidebarCollapsed} onToggle={onToggleSidebar} />

              {/* Breadcrumb: Patient Overview >> Visit Note */}
              <nav style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  onClick={() => navigate(`/patient/${patientId}/encounters`)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: primary,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    cursor: 'pointer',
                    padding: 0,
                    whiteSpace: 'nowrap',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  Patient Overview
                </button>
                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
                  <NavIcon name="chevron-right" />
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--text)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Visit Note
                </span>
              </nav>

              <StatusBadge status={encounter.status} />

              {/* PRD.217 — manual consult lifecycle badge */}
              <ConsultLifecycleBadge encounter={encounter} />
            </div>

            {/* Subtitle — the elastic element: it ellipsises so the actions to
              its right are never pushed off the edge. */}
            <span className="encounter-header-sub">
              {typeLabel} · {formatDate(encounter.started_at)} · {formatTime(encounter.started_at)}{' '}
              · {encounter.provider_name ?? 'Unassigned'}
            </span>

            <div className="encounter-header-actions">
              {/* PRD.217 — Start Consult / End Consult */}
              <ConsultLifecycleAction encounter={encounter} db={db} />
              {/* PRD.264 — Used Supplies action, sits between Consult and Billing. */}
              {layoutConfig.style === 'guided_note' && (
                <button
                  onClick={() => setUsedSuppliesPanelOpen(true)}
                  title="Record Used Supplies"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    height: 32,
                    padding: '0 10px',
                    borderRadius: 'var(--radius-full)',
                    border: '1px solid var(--border)',
                    background: 'var(--bg-surface)',
                    color: 'var(--text)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  <NavIcon name="package" />
                  Used Supplies
                </button>
              )}
              {/* Billing chip — guided note only (PRD.163). Hidden when the clinic
                Invoice module is disabled (PRD.283). */}
              {layoutConfig.style === 'guided_note' && clinicInvoiceEnabled && (
                <VisitBillingChip
                  patientId={patientId ?? ''}
                  encounterId={encounterId ?? ''}
                  onClick={() => setBillingPanelOpen(true)}
                />
              )}
              {/* Settings button */}
              <button
                onClick={() => setSettingsOpen(true)}
                title="Visit Note Settings"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-muted)',
                  flexShrink: 0,
                }}
              >
                <NavIcon name="gear" />
              </button>
              {/* Delete button */}
              <button
                onClick={() => setDeleteModalOpen(true)}
                title="Delete Visit Note"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid #fecaca',
                  background: '#fef2f2',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#dc2626',
                  flexShrink: 0,
                }}
              >
                <NavIcon name="trash" />
              </button>
            </div>
          </div>

          {/* Tabs — only in tabs mode. Scrolls horizontally when the strip is
            wider than the header (see `.encounter-tabs`). */}
          {layoutConfig.style === 'tabs' && (
            <div className="encounter-tabs">
              {visibleTabs.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as TabKey)}
                    style={{
                      padding: '8px 16px',
                      background: 'none',
                      border: 'none',
                      borderBottom: isActive ? `2px solid ${primary}` : '2px solid transparent',
                      color: isActive ? 'var(--text)' : 'var(--text-muted)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: isActive
                        ? 'var(--font-weight-semi)'
                        : 'var(--font-weight-medium)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Content area */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {layoutConfig.style === 'tabs' ? (
          // ── Tabs mode ──
          <>
            {activeTab === 'summary' && (
              <SummaryTab encounter={encounter} appointment={appointment} />
            )}
            {activeTab === 'chief_complaint' && <ChiefComplaintSection encounter={encounter} />}
            {activeTab === 'charting' && (
              <EncounterChartingTab
                patientId={patientId ?? ''}
                encounterId={encounterId ?? ''}
                visitId={encounter.visit_id}
              />
            )}
            {activeTab === 'notes' && (
              <EncounterNotesTab
                patientId={patientId ?? ''}
                encounterId={encounterId ?? ''}
                visitId={encounter.visit_id}
              />
            )}
            {activeTab === 'symptoms' && (
              <EncounterSymptomsTab patientId={patientId ?? ''} encounterId={encounterId ?? ''} />
            )}
            {activeTab === 'diagnoses' && (
              <EncounterDiagnosesTab patientId={patientId ?? ''} encounterId={encounterId ?? ''} />
            )}
            {activeTab === 'vitals' && (
              <EncounterVitalsTab patientId={patientId ?? ''} encounterId={encounterId ?? ''} />
            )}
            {activeTab === 'prescriptions' && (
              <EncounterPrescriptionsTab
                patientId={patientId ?? ''}
                encounterId={encounterId ?? ''}
                visitId={encounter.visit_id}
              />
            )}
            {activeTab === 'treatment_plan' && <PlaceholderTab title="Treatment Plan" />}
            {activeTab === 'forms' && (
              <div style={{ padding: 'var(--sp-5)' }}>
                <EncounterFormsTab
                  patientId={patientId ?? ''}
                  encounterId={encounterId ?? ''}
                  visitId={encounter.visit_id}
                />
              </div>
            )}
            {activeTab === 'files' && (
              <div style={{ padding: 'var(--sp-5)' }}>
                <FilesTab
                  patientId={patientId ?? ''}
                  encounterId={encounterId}
                  visitId={encounter.visit_id ?? undefined}
                />
              </div>
            )}
            {activeTab === 'invoices' && clinicInvoiceEnabled && (
              <div style={{ padding: 'var(--sp-5)' }}>
                <InvoicesTab
                  patientId={patientId ?? ''}
                  encounterId={encounterId}
                  visitId={encounter.visit_id}
                  initialInvoiceId={searchParams.get('invoiceId') ?? undefined}
                />
              </div>
            )}
            {activeTab === 'pharmacy' && (
              <EncounterPharmacyTab patientId={patientId ?? ''} encounterId={encounterId ?? ''} />
            )}
            {activeTab === 'labs' && (
              <div style={{ padding: 'var(--sp-5)' }}>
                <EncounterLabsTab
                  patientId={patientId ?? ''}
                  encounterId={encounterId ?? ''}
                  visitId={encounter.visit_id}
                  providerId={encounter.provider_id ?? null}
                />
              </div>
            )}
            {activeTab === 'payments' && <PlaceholderTab title="Payments" />}
          </>
        ) : layoutConfig.style === 'guided_note' ? (
          // ── Guided Note mode ──
          <GuidedNoteRenderer
            preset={layoutConfig.guidedNotePreset}
            visibleTabs={guidedVisibleTabs}
            primary={primary}
            renderHeroSection={(cardId) => {
              if (cardId === 'charting-images') {
                return (
                  <DentalChartingHero
                    patientId={patientId ?? ''}
                    encounterId={encounterId ?? ''}
                    visitId={encounter.visit_id}
                    primary={primary}
                  />
                );
              }
              if (cardId === 'visual-assessment' || cardId === 'visual-assessment-basic') {
                return (
                  <AestheticVisualHero
                    patientId={patientId ?? ''}
                    encounterId={encounterId ?? ''}
                    visitId={encounter.visit_id}
                    primary={primary}
                    // General reuses the aesthetic workspace without Before/After.
                    showBeforeAfter={cardId === 'visual-assessment'}
                    // AI Analyze is Aesthetic-only; General + Dental hide it.
                    showAiAnalyze={cardId === 'visual-assessment'}
                  />
                );
              }
              return undefined;
            }}
            renderSubmodule={(key) => {
              switch (key) {
                case 'summary':
                  return <SummaryTab encounter={encounter} appointment={appointment} />;
                case 'chief_complaint':
                  return <ChiefComplaintSection encounter={encounter} />;
                case 'charting':
                  return (
                    <EncounterChartingTab
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                      visitId={encounter.visit_id}
                    />
                  );
                case 'notes':
                  return (
                    <EncounterNotesTab
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                      visitId={encounter.visit_id}
                      hideHeader
                      quietActions
                    />
                  );
                case 'symptoms':
                  return (
                    <EncounterSymptomsTab
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                      hideHeader
                    />
                  );
                case 'diagnoses':
                  return (
                    <EncounterDiagnosesTab
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                      hideHeader
                    />
                  );
                case 'vitals':
                  return (
                    <EncounterVitalsTab
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                    />
                  );
                case 'prescriptions':
                  return (
                    <EncounterPrescriptionsTab
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                      visitId={encounter.visit_id}
                      hideHeader
                      quietActions
                    />
                  );
                case 'treatment_plan':
                  return (
                    <TreatmentPlanSection
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                      primary={primary}
                      hideHeader
                    />
                  );
                case 'forms':
                  return (
                    <div style={{ padding: 'var(--sp-5)' }}>
                      <EncounterFormsTab
                        patientId={patientId ?? ''}
                        encounterId={encounterId ?? ''}
                        visitId={encounter.visit_id}
                        quietActions
                      />
                    </div>
                  );
                case 'files':
                  return (
                    <div style={{ padding: 'var(--sp-5)' }}>
                      <FilesTab
                        patientId={patientId ?? ''}
                        encounterId={encounterId}
                        visitId={encounter.visit_id ?? undefined}
                      />
                    </div>
                  );
                case 'invoices':
                  return (
                    <GuidedInvoiceSummary
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                      visitId={encounter.visit_id}
                    />
                  );
                case 'pharmacy':
                  return (
                    <EncounterPharmacyTab
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                      hideHeader
                    />
                  );
                case 'labs':
                  return (
                    <div style={{ padding: 'var(--sp-5)' }}>
                      <EncounterLabsTab
                        patientId={patientId ?? ''}
                        encounterId={encounterId ?? ''}
                        visitId={encounter.visit_id}
                        providerId={encounter.provider_id ?? null}
                      />
                    </div>
                  );
                case 'payments':
                  return (
                    <GuidedPaymentSummary
                      patientId={patientId ?? ''}
                      encounterId={encounterId ?? ''}
                    />
                  );
                default:
                  return <PlaceholderTab title={key} />;
              }
            }}
          />
        ) : (
          // ── Accordion mode ──
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {visibleTabs.map((tab) => {
              const isOpen = openSections.has(tab.key);
              const icon = ACCORDION_ICONS[tab.key] ?? 'file-text';
              const toggleSection = () => {
                setOpenSections((prev) => {
                  const next = new Set(prev);
                  if (next.has(tab.key)) {
                    next.delete(tab.key);
                  } else {
                    if (layoutConfig.accordionMode === 'single') {
                      next.clear();
                    }
                    next.add(tab.key);
                  }
                  return next;
                });
              };
              return (
                <div key={tab.key} id={`encounter-section-${tab.key}`}>
                  {/* Accordion header bar */}
                  <button
                    onClick={toggleSection}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      padding: '14px var(--sp-6)',
                      background: isOpen ? 'var(--bg-surface-2)' : 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--border)',
                      borderTop: '1px solid var(--border)',
                      marginTop: -1,
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 150ms',
                    }}
                    onMouseEnter={(e) => {
                      if (!isOpen) e.currentTarget.style.background = 'var(--bg-surface-2)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isOpen) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={isOpen ? primary : 'var(--text-muted)'}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 150ms',
                        flexShrink: 0,
                      }}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                    <span
                      style={{
                        color: isOpen ? primary : 'var(--text-muted)',
                        flexShrink: 0,
                        display: 'flex',
                      }}
                    >
                      <NavIcon name={icon} />
                    </span>
                    <span
                      style={{
                        flex: 1,
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        color: isOpen ? 'var(--text)' : 'var(--text-muted)',
                        letterSpacing: '-0.1px',
                      }}
                    >
                      {tab.label}
                    </span>
                    {!isOpen && (
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-placeholder)',
                        }}
                      >
                        Click to expand
                      </span>
                    )}
                  </button>
                  {/* Accordion content */}
                  {isOpen && (
                    <div style={{ borderBottom: '2px solid var(--border)' }}>
                      {tab.key === 'summary' && (
                        <SummaryTab encounter={encounter} appointment={appointment} />
                      )}
                      {tab.key === 'chief_complaint' && (
                        <ChiefComplaintSection encounter={encounter} />
                      )}
                      {tab.key === 'charting' && (
                        <EncounterChartingTab
                          patientId={patientId ?? ''}
                          encounterId={encounterId ?? ''}
                          visitId={encounter.visit_id}
                        />
                      )}
                      {tab.key === 'notes' && (
                        <EncounterNotesTab
                          patientId={patientId ?? ''}
                          encounterId={encounterId ?? ''}
                          visitId={encounter.visit_id}
                        />
                      )}
                      {tab.key === 'symptoms' && (
                        <EncounterSymptomsTab
                          patientId={patientId ?? ''}
                          encounterId={encounterId ?? ''}
                        />
                      )}
                      {tab.key === 'diagnoses' && (
                        <EncounterDiagnosesTab
                          patientId={patientId ?? ''}
                          encounterId={encounterId ?? ''}
                        />
                      )}
                      {tab.key === 'vitals' && (
                        <EncounterVitalsTab
                          patientId={patientId ?? ''}
                          encounterId={encounterId ?? ''}
                        />
                      )}
                      {tab.key === 'prescriptions' && (
                        <EncounterPrescriptionsTab
                          patientId={patientId ?? ''}
                          encounterId={encounterId ?? ''}
                          visitId={encounter.visit_id}
                        />
                      )}
                      {tab.key === 'treatment_plan' && <PlaceholderTab title="Treatment Plan" />}
                      {tab.key === 'forms' && (
                        <div style={{ padding: 'var(--sp-5)' }}>
                          <EncounterFormsTab
                            patientId={patientId ?? ''}
                            encounterId={encounterId ?? ''}
                            visitId={encounter.visit_id}
                          />
                        </div>
                      )}
                      {tab.key === 'files' && (
                        <div style={{ padding: 'var(--sp-5)' }}>
                          <FilesTab
                            patientId={patientId ?? ''}
                            encounterId={encounterId}
                            visitId={encounter.visit_id ?? undefined}
                          />
                        </div>
                      )}
                      {tab.key === 'invoices' && (
                        <div style={{ padding: 'var(--sp-5)' }}>
                          <InvoicesTab
                            patientId={patientId ?? ''}
                            encounterId={encounterId}
                            visitId={encounter.visit_id}
                            initialInvoiceId={searchParams.get('invoiceId') ?? undefined}
                          />
                        </div>
                      )}
                      {tab.key === 'pharmacy' && (
                        <EncounterPharmacyTab
                          patientId={patientId ?? ''}
                          encounterId={encounterId ?? ''}
                          hideHeader
                        />
                      )}
                      {tab.key === 'labs' && (
                        <div style={{ padding: 'var(--sp-5)' }}>
                          <EncounterLabsTab
                            patientId={patientId ?? ''}
                            encounterId={encounterId ?? ''}
                            visitId={encounter.visit_id}
                            providerId={encounter.provider_id ?? null}
                          />
                        </div>
                      )}
                      {tab.key === 'payments' && <PlaceholderTab title="Payments" />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <VisitNoteSettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={tabSettings}
        onSave={updateSettings}
        onReset={resetToDefault}
        primary={primary}
        // PRD.300 — the drawer offers toggles only for sub-modules that are
        // actually available (clinic config ∩ feature flags ∩ RBAC), so a user
        // can never save a preference naming a module they can't see.
        clinicVisibleConsultKeys={availableKeys}
        defaultGuidedNotePreset={defaultGuidedNotePreset}
      />

      <VisitBillingPanel
        open={billingPanelOpen && clinicInvoiceEnabled}
        onClose={() => setBillingPanelOpen(false)}
        patientId={patientId ?? ''}
        encounterId={encounterId ?? ''}
        visitId={encounter.visit_id}
      />

      {/* PRD.264 — Used Supplies drawer. */}
      <UsedSuppliesPanel
        open={usedSuppliesPanelOpen}
        onClose={() => setUsedSuppliesPanelOpen(false)}
        patientId={patientId ?? ''}
        encounterId={encounterId ?? ''}
        visitId={encounter.visit_id ?? null}
      />

      <DeleteEncounterModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteEncounter}
        hasLinkedAppointment={!!encounter.appointment_id}
        hasLinkedQueueEntry={!!encounter.queue_id}
        isDeleting={isDeleting}
      />
    </div>
  );
}
